import React, { useState } from 'react';
import './poster_wizard.css';
import WizardSelectPrincipleForm from './wizard_select_principle_form';
import WizardSelectActualPrinciplesForm from './wizard_select_actual_principles_form';
import WizardSelectPeopleDetailsForm from './wizard_select_people_details_form';
import WizardSelectCustomizationForm from './wizard_select_customization_form';
import WizardPreviewSetForm from './wizard_preview_set_form';
import WizardErrorCorrectionFeedbackSourceForm from './wizard_error_correction_feedback_source_form';
import WizardErrorCorrectionFeedbackTargetForm from './wizard_error_correction_feedback_target_form';
import WizardErrorCorrectionPreviewForm from './wizard_error_correction_preview_form';
import { 
    WizardSelectCountryForm, 
    WizardEnterDetailsForm, 
    WizardSelectGivenSetPreferencesForm, 
    WizardPreviewMapForm 
} from './country_map';
import { showAlert } from '../../../functions/show_alert';
import AppBarWidget from '../../../widgets/app_bar/app_bar';

// Poster wizard steps
const STEPS = [
    { number: 1, title: 'Select Principle' },
    { number: 2, title: 'Select Actual Principles' },
    { number: 3, title: 'Select People Details' },
    { number: 4, title: 'Select Customization' },
    { number: 5, title: 'Preview Set' }
];

// Error Correction wizard steps
const ERROR_CORRECTION_STEPS = [
    { number: 1, title: 'Add Feedback Source Details' },
    { number: 2, title: 'Add Feedback Target Details' },
    { number: 3, title: 'Select Principle' },
    { number: 4, title: 'Preview Set' }
];

// Country Map wizard steps
const COUNTRY_MAP_STEPS = [
    { number: 1, title: 'Select Country' },
    { number: 2, title: 'Enter Details' },
    { number: 3, title: 'Select The Given Set Preferences' },
    { number: 4, title: 'Preview Map' }
];

export default function PosterWizard({ onClose, onComplete, posterType }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [principleData, setPrincipleData] = useState({});
    const [actualPrinciplesData, setActualPrinciplesData] = useState({});
    const [peopleDetailsData, setPeopleDetailsData] = useState({});
    const [customizationData, setCustomizationData] = useState({});
    const [feedbackSourceData, setFeedbackSourceData] = useState({});
    const [feedbackTargetData, setFeedbackTargetData] = useState({});
    // Country Map specific state
    const [countryData, setCountryData] = useState({ selectedCountry: 'usa_mainland' });
    const [detailsData, setDetailsData] = useState({});
    const [preferencesData, setPreferencesData] = useState({ selectedPreferences: [] });

    // Check if this is Error Correction wizard
    const isErrorCorrection = posterType === 'Error Correction';
    // Check if this is Country Map wizard
    const isCountryMap = posterType === 'Country Map';

    // Principles that use the simplified flow (skip steps 2 and 4)
    const SIMPLIFIED_FLOW_PRINCIPLES = [
        'The Given Set',
        'Parent',
        'Our Parent',
        'Operating Principle',
        'Main Set',
        'Mother Nature'
    ];
    
    // Check if a simplified flow principle is selected
    const isSimplifiedFlowPrinciple = SIMPLIFIED_FLOW_PRINCIPLES.includes(principleData.selectedPrinciple);
    
    // Check if "The Given Set" is selected (for backward compatibility)
    const isGivenSetSelected = isSimplifiedFlowPrinciple;
    
    // Check if "Show Derived Principle" is selected
    const isShowDerivedPrinciple = principleData.selectedPrinciple === 'Show Derived Principle';

    // Get the next step number based on current step and selected principle
    const getNextStep = (step) => {
        if (isCountryMap) {
            // For Country Map: Step 1 -> Step 2 -> Step 3 -> Step 4
            if (step < COUNTRY_MAP_STEPS.length) return step + 1;
            return null; // Complete
        } else if (isErrorCorrection) {
            // For Error Correction: Step 1 -> Step 2 -> Step 3 -> Step 4
            if (step < ERROR_CORRECTION_STEPS.length) return step + 1;
            return null; // Complete
        } else if (isGivenSetSelected) {
            // For simplified flow principles: Step 1 -> Step 3 -> Step 5
            if (step === 1) return 3; // Skip step 2 (Select Actual Principles)
            if (step === 3) return 5; // Skip step 4 (Select Customization)
            if (step === 5) return null; // Complete
        } else {
            // For other principles: normal flow Step 1 -> Step 2 -> Step 3 -> Step 4 -> Step 5
            if (step < STEPS.length) return step + 1;
            return null; // Complete
        }
        return null;
    };

    // Get the previous step number based on current step and selected principle
    const getPreviousStep = (step) => {
        if (isCountryMap) {
            // For Country Map: normal flow backwards
            if (step > 1) return step - 1;
            return null;
        } else if (isErrorCorrection) {
            // For Error Correction: normal flow backwards
            if (step > 1) return step - 1;
            return null;
        } else if (isGivenSetSelected) {
            // For simplified flow principles: Step 5 -> Step 3 -> Step 1
            if (step === 5) return 3; // Skip step 4 (Select Customization)
            if (step === 3) return 1; // Skip step 2 (Select Actual Principles)
            if (step === 1) return null; // Can't go back from first step
        } else {
            // For other principles: normal flow
            if (step > 1) return step - 1;
            return null;
        }
        return null;
    };

    // Get visible steps based on selected principle
    const getVisibleSteps = () => {
        if (isCountryMap) {
            return COUNTRY_MAP_STEPS;
        } else if (isErrorCorrection) {
            return ERROR_CORRECTION_STEPS;
        } else if (isGivenSetSelected) {
            // For simplified flow principles: show only steps 1, 3, and 5
            return STEPS.filter(step => step.number === 1 || step.number === 3 || step.number === 5);
        }
        return STEPS;
    };

    // Get step title dynamically based on selected principle
    const getStepTitle = (stepNumber) => {
        if (isCountryMap) {
            const step = COUNTRY_MAP_STEPS.find(s => s.number === stepNumber);
            return step ? step.title : '';
        } else if (isErrorCorrection) {
            const step = ERROR_CORRECTION_STEPS.find(s => s.number === stepNumber);
            return step ? step.title : '';
        } else if (stepNumber === 4 && isShowDerivedPrinciple) {
            return 'Type Principle';
        }
        const step = STEPS.find(s => s.number === stepNumber);
        return step ? step.title : '';
    };

    const handleNext = () => {
        if (isCountryMap) {
            // Country Map validation
            if (currentStep === 1) {
                // Validate country selection
                if (!countryData.selectedCountry) {
                    showAlert(false, 'Please select a country');
                    return;
                }
            } else if (currentStep === 2) {
                // Validate details (person name required)
                if (!detailsData.personName || detailsData.personName.trim() === '') {
                    showAlert(false, 'Please enter a person name');
                    return;
                }
            } else if (currentStep === 3) {
                // No required validation for preferences - checkboxes are optional
            }
        } else if (isErrorCorrection) {
            // Error Correction validation
            if (currentStep === 1) {
                // Validate feedback source (text or image required)
                if ((!feedbackSourceData.text || feedbackSourceData.text.trim() === '') && !feedbackSourceData.image) {
                    showAlert(false, 'Please add text or upload an image for feedback source');
                    return;
                }
            } else if (currentStep === 2) {
                // Validate feedback target (text or image required)
                if ((!feedbackTargetData.text || feedbackTargetData.text.trim() === '') && !feedbackTargetData.image) {
                    showAlert(false, 'Please add text or upload an image for feedback target');
                    return;
                }
            } else if (currentStep === 3) {
                // Validate principle selection
                if (!principleData.selectedPrinciple) {
                    showAlert(false, 'Please select a principle');
                    return;
                }
            }
        } else {
            // Original validation logic
            if (currentStep === 1) {
                // Validate principle selection
                if (!principleData.selectedPrinciple) {
                    showAlert(false, 'Please select a principle');
                    return;
                }
            } else if (currentStep === 2 && !isGivenSetSelected) {
                // Validate actual principles (only if not "The Given Set")
                if (!actualPrinciplesData.selectedActualPrinciples || actualPrinciplesData.selectedActualPrinciples.length === 0) {
                    showAlert(false, 'Please select at least one actual principle');
                    return;
                }
            } else if (currentStep === 3) {
                // Validate people details
                if (!peopleDetailsData.people || peopleDetailsData.people.length === 0) {
                    showAlert(false, 'Please add at least one person');
                    return;
                }
                // Check if all people have names
                const hasEmptyNames = peopleDetailsData.people.some(person => !person.name || person.name.trim() === '');
                if (hasEmptyNames) {
                    showAlert(false, 'Please enter a name for all people');
                    return;
                }
            } else if (currentStep === 4 && !isGivenSetSelected) {
                // Validate customization (only if not "The Given Set")
                if (isShowDerivedPrinciple) {
                    // For "Show Derived Principle", validate text input
                    if (!customizationData.principleText || customizationData.principleText.trim() === '' || customizationData.principleText === '<p><br></p>') {
                        showAlert(false, 'Please enter the principle text');
                        return;
                    }
                } else {
                    // For other principles, validate checkbox selections
                    if (!customizationData.selectedCustomizations || customizationData.selectedCustomizations.length === 0) {
                        showAlert(false, 'Please select at least one customization option');
                        return;
                    }
                }
            }
        }

        const nextStep = getNextStep(currentStep);
        if (nextStep) {
            setCurrentStep(nextStep);
        } else {
            // Wizard complete
            handleComplete();
        }
    };

    const handlePrincipleDataChange = (data) => {
        setPrincipleData(data);
    };

    const handleActualPrinciplesDataChange = (data) => {
        setActualPrinciplesData(data);
    };

    const handlePeopleDetailsDataChange = (data) => {
        setPeopleDetailsData(data);
    };

    const handleCustomizationDataChange = (data) => {
        setCustomizationData(data);
    };

    const handleFeedbackSourceDataChange = (data) => {
        setFeedbackSourceData(data);
    };

    const handleFeedbackTargetDataChange = (data) => {
        setFeedbackTargetData(data);
    };

    const handleCountryDataChange = (data) => {
        setCountryData(data);
    };

    const handleDetailsDataChange = (data) => {
        setDetailsData(data);
    };

    const handlePreferencesDataChange = (data) => {
        setPreferencesData(data);
    };

    const handleBack = () => {
        const previousStep = getPreviousStep(currentStep);
        if (previousStep) {
            setCurrentStep(previousStep);
        }
    };

    const handleComplete = () => {
        if (onComplete) {
            if (isCountryMap) {
                onComplete({
                    type: 'Country Map',
                    countryData: countryData,
                    detailsData: detailsData,
                    preferencesData: preferencesData
                });
            } else if (isErrorCorrection) {
                onComplete({
                    type: 'Error Correction',
                    feedbackSourceData: feedbackSourceData,
                    feedbackTargetData: feedbackTargetData,
                    principleData: principleData
                });
            } else {
                onComplete({
                    principleData: principleData,
                    actualPrinciplesData: actualPrinciplesData,
                    peopleDetailsData: peopleDetailsData,
                    customizationData: customizationData
                });
            }
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    const renderStepContent = () => {
        if (isCountryMap) {
            // Country Map flow
            switch (currentStep) {
                case 1:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectCountryForm
                                onDataChange={handleCountryDataChange}
                                initialData={countryData}
                            />
                        </div>
                    );
                case 2:
                    return (
                        <div className="wizard-step-content">
                            <WizardEnterDetailsForm
                                countryData={countryData}
                                onDataChange={handleDetailsDataChange}
                                initialData={detailsData}
                            />
                        </div>
                    );
                case 3:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectGivenSetPreferencesForm
                                countryData={countryData}
                                detailsData={detailsData}
                                onDataChange={handlePreferencesDataChange}
                                initialData={preferencesData}
                            />
                        </div>
                    );
                case 4:
                    return (
                        <div className="wizard-step-content">
                            <WizardPreviewMapForm
                                countryData={countryData}
                                detailsData={detailsData}
                                preferencesData={preferencesData}
                                onDataChange={handleDetailsDataChange}
                            />
                        </div>
                    );
                default:
                    return null;
            }
        } else if (isErrorCorrection) {
            // Error Correction flow
            switch (currentStep) {
                case 1:
                    return (
                        <div className="wizard-step-content">
                            <WizardErrorCorrectionFeedbackSourceForm
                                onDataChange={handleFeedbackSourceDataChange}
                                initialData={feedbackSourceData}
                            />
                        </div>
                    );
                case 2:
                    return (
                        <div className="wizard-step-content">
                            <WizardErrorCorrectionFeedbackTargetForm
                                onDataChange={handleFeedbackTargetDataChange}
                                initialData={feedbackTargetData}
                            />
                        </div>
                    );
                case 3:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectPrincipleForm
                                onDataChange={handlePrincipleDataChange}
                                initialData={principleData}
                            />
                        </div>
                    );
                case 4:
                    return (
                        <div className="wizard-step-content">
                            <WizardErrorCorrectionPreviewForm
                                feedbackSourceData={feedbackSourceData}
                                feedbackTargetData={feedbackTargetData}
                                principleData={principleData}
                            />
                        </div>
                    );
                default:
                    return null;
            }
        } else {
            // Original flow
            switch (currentStep) {
                case 1:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectPrincipleForm
                                onDataChange={handlePrincipleDataChange}
                                initialData={principleData}
                            />
                        </div>
                    );
                case 2:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectActualPrinciplesForm
                                principleData={principleData}
                                onDataChange={handleActualPrinciplesDataChange}
                                initialData={actualPrinciplesData}
                            />
                        </div>
                    );
                case 3:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectPeopleDetailsForm
                                principleData={principleData}
                                actualPrinciplesData={actualPrinciplesData}
                                onDataChange={handlePeopleDetailsDataChange}
                                initialData={peopleDetailsData}
                            />
                        </div>
                    );
                case 4:
                    return (
                        <div className="wizard-step-content">
                            <WizardSelectCustomizationForm
                                principleData={principleData}
                                actualPrinciplesData={actualPrinciplesData}
                                peopleDetailsData={peopleDetailsData}
                                onDataChange={handleCustomizationDataChange}
                                initialData={customizationData}
                            />
                        </div>
                    );
                case 5:
                    return (
                        <div className="wizard-step-content">
                            <WizardPreviewSetForm
                                principleData={principleData}
                                actualPrinciplesData={actualPrinciplesData}
                                peopleDetailsData={peopleDetailsData}
                                customizationData={customizationData}
                                onDataChange={handleCustomizationDataChange}
                                initialData={{}}
                            />
                        </div>
                    );
                default:
                    return null;
            }
        }
    };

    const visibleSteps = getVisibleSteps();
    const getTotalSteps = () => {
        if (isCountryMap) return COUNTRY_MAP_STEPS.length;
        if (isErrorCorrection) return ERROR_CORRECTION_STEPS.length;
        // For simplified flow, return the number of visible steps (1, 3, 5 = 3 steps)
        // For normal flow, return all steps (1, 2, 3, 4, 5 = 5 steps)
        return visibleSteps.length;
    };

    // Get the current step position in the visible steps array (for progress calculation)
    const getCurrentStepPosition = () => {
        if (isCountryMap) {
            return currentStep; // Country map uses sequential steps
        } else if (isErrorCorrection) {
            return currentStep; // Error correction uses sequential steps
        } else if (isGivenSetSelected) {
            // For simplified flow: step 1 = position 1, step 3 = position 2, step 5 = position 3
            if (currentStep === 1) return 1;
            if (currentStep === 3) return 2;
            if (currentStep === 5) return 3;
            return currentStep;
        } else {
            return currentStep; // Normal flow uses sequential steps
        }
    };

    return (
        <>
            <AppBarWidget />
            <div className="poster-wizard-screen">
                <div className="poster-wizard-container">
                    <div className="wizard-header">
                        <h1 className="wizard-title">Create Poster</h1>
                    </div>

                    {/* Desktop Stepper */}
                    <div className="wizard-steps-indicator desktop-stepper">
                        {visibleSteps.map((step, index) => {
                            let isStepCompleted;
                            if (isCountryMap) {
                                isStepCompleted = currentStep > step.number;
                            } else if (isErrorCorrection) {
                                isStepCompleted = currentStep > step.number;
                            } else if (isGivenSetSelected) {
                                isStepCompleted = (step.number === 1 && currentStep > 1) || 
                                  (step.number === 3 && currentStep > 3) ||
                                  (step.number === 5 && currentStep > 5);
                            } else {
                                isStepCompleted = currentStep > step.number;
                            }
                            const stepTitle = getStepTitle(step.number);
                            return (
                                <React.Fragment key={step.number}>
                                    <div className={`step-indicator ${currentStep === step.number ? 'active' : isStepCompleted ? 'completed' : ''}`}>
                                        <div className="step-number">{step.number}</div>
                                        <div className="step-title">{stepTitle}</div>
                                    </div>
                                    {index < visibleSteps.length - 1 && (
                                        <div className={`step-connector ${isStepCompleted ? 'completed' : ''}`}></div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Mobile Stepper */}
                    <div className="mobile-stepper">
                        <div className="mobile-stepper-content">
                            <div className="mobile-stepper-circle">
                                <svg className="mobile-stepper-svg" viewBox="0 0 100 100">
                                    {/* Background circle */}
                                    <circle
                                        className="mobile-stepper-bg"
                                        cx="50"
                                        cy="50"
                                        r="40"
                                        fill="none"
                                        stroke="#E1E1E1"
                                        strokeWidth="5"
                                    />
                                    {/* Progress circle */}
                                    <circle
                                        className="mobile-stepper-progress"
                                        cx="50"
                                        cy="50"
                                        r="40"
                                        fill="none"
                                        stroke="#00A14C"
                                        strokeWidth="5"
                                        strokeLinecap="round"
                                        strokeDasharray={`${2 * Math.PI * 40}`}
                                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - getCurrentStepPosition() / getTotalSteps())}`}
                                        transform="rotate(-90 50 50)"
                                    />
                                </svg>
                                <div className="mobile-stepper-text">
                                    <span className="mobile-stepper-current">{getCurrentStepPosition()}</span>
                                    <span className="mobile-stepper-separator"> of </span>
                                    <span className="mobile-stepper-total">{getTotalSteps()}</span>
                                </div>
                            </div>
                            <div className="mobile-stepper-labels">
                                <div className="mobile-stepper-current-label">
                                    {getStepTitle(currentStep)}
                                </div>
                                {getNextStep(currentStep) !== null && (
                                    <div className="mobile-stepper-next-label">
                                        Next: {getStepTitle(getNextStep(currentStep))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="wizard-content">
                        {renderStepContent()}
                    </div>

                    <div className="wizard-footer">
                        <button className={`wizard-button cancel ${currentStep === 1 ? 'show-on-mobile' : 'hide-on-mobile'}`} onClick={handleCancel}>
                            Cancel
                        </button>
                        {getPreviousStep(currentStep) !== null && (
                            <button className="wizard-button back" onClick={handleBack}>
                                Previous
                            </button>
                        )}
                        <button className="wizard-button next" onClick={handleNext}>
                            {getNextStep(currentStep) === null ? 'Create' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
